import { useState, useCallback, type ReactNode } from 'react';
import { AdminAuthContext } from './AdminAuthContext';

// Persisted in localStorage (not localStorage) so the admin "logged in" hint
// is shared across tabs — opening an admin link (e.g. "Preview as customer") in
// a new tab honours the existing session instead of bouncing to login. The real
// session is the HttpOnly admin_session_id cookie (also shared across tabs); a
// stale flag self-heals via handleUnauthorized on the next 401/403.
const SESSION_KEY = 'loupe_admin_authenticated';

/** The stored hint, or false where storage is unavailable (e.g. blocked). */
function readStoredFlag(): boolean {
  try {
    return localStorage.getItem(SESSION_KEY) === 'true';
  } catch {
    return false;
  }
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  // Read once, before the first render. The hint is local, so there's nothing
  // to wait for; `loading` stays in the context for callers that check it.
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(readStoredFlag);
  const loading = false;
  const [error, setError] = useState<string | null>(null);

  const login = useCallback(async (password: string, totpCode?: string) => {
    setError(null);
    try {
      const res = await fetch('/api/v1/admin/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        // `totp_code` is omitted by JSON.stringify when undefined, so this stays
        // compatible with the password-only flow before MFA is enrolled.
        body: JSON.stringify({ password, totp_code: totpCode || undefined }),
      });

      if (res.ok) {
        localStorage.setItem(SESSION_KEY, 'true');
        setIsAdminAuthenticated(true);
      } else if (res.status === 401) {
        setError('Invalid credentials');
      } else if (res.status === 429) {
        setError('Too many failed attempts. Please wait and try again.');
      } else {
        setError('Login temporarily unavailable');
      }
    } catch {
      setError('Login temporarily unavailable');
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch('/api/v1/admin/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } finally {
      localStorage.removeItem(SESSION_KEY);
      setIsAdminAuthenticated(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem(SESSION_KEY);
    setIsAdminAuthenticated(false);
  }, []);

  return (
    <AdminAuthContext.Provider
      value={{ isAdminAuthenticated, loading, error, login, logout, clearError, handleUnauthorized }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}
